import * as React from 'react';
import { styled } from '@mui/system';
import { Card, Button } from '@mui/material';
import Link from '@mui/material/Link';
import ThumbUpOffAltIcon from '@mui/icons-material/ThumbUpOffAlt';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import ThumbUpAltIcon from '@mui/icons-material/ThumbUpAlt';
import ClassOutlinedIcon from '@mui/icons-material/ClassOutlined';
import BookIcon from '@mui/icons-material/Book';
import { toMonthShortName } from '../../../../../utils/date';
import {
  useLikeNoteMutation,
  useUnlikeNoteMutation,
  useSaveNoteMutation,
  useUnsaveNoteMutation,
} from '../../../../../store/api/noteApi';
import { useSelector } from 'react-redux';
import { selectUser } from '../../../../../store/auth/authSlice';

const NoteCardContainer = styled(Card)(({ theme }) => ({
  width: '96%',
  background: 'transparent',
  border: '1px solid #FFFFFF',
  margin: 'auto',
  borderRadius: '4px',
  padding: '16px 16px 5px',
  marginBottom: '20px',

  [theme.breakpoints.down('600')]: {
    width: '100%',
    marginBottom: '0px',
    padding: '0px',
    boxShadow: '0px 0px !important',
    borderRadius: '0px',
    border: '1px solid #AB9178',
  },
}));

const EffectLayer = styled('span')({
  position: 'absolute',
  width: '100%',
  height: '100%',
  top: '0px',
  left: '0px',
  display: 'block',
});

const ShadowLayer = styled('div')(({ theme }) => ({
  display: 'block',
  background: 'linear-gradient(180.23deg, rgba(77, 67, 67, 0) 62.73%, rgba(0, 0, 0, 0.90) 90%);',
  height: '100%',
  borderRadius: '4px 4px 4px 4px',

  [theme.breakpoints.down('600')]: {
    borderRadius: '0px',
  },
}));

const YellowLine = styled('span')(({ theme }) => ({
  border: '1px solid #AB9178',
  position: 'absolute',
  width: '94%',
  height: '94%',
  top: '3%',
  left: '3%',
  display: 'block',
  [theme.breakpoints.down('600')]: {
    border: 'none',
  },
}));

const NoteAuthor = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  fontSize: '16px',
  color: 'black',
  lineHeight: '25px',
  margin: '0 5px 12px',
  fontFamily: 'Fjalla One',

  [theme.breakpoints.down('1200')]: {
    fontSize: '16px',
    lineHeight: '20px',
  },

  [theme.breakpoints.down('1000')]: {
    fontSize: '16px',
    lineHeight: '20px',
  },

  [theme.breakpoints.down('900')]: {
    fontSize: '16px',
    lineHeight: '20px',
  },

  [theme.breakpoints.down('800')]: {
    fontSize: '18px',
  },

  [theme.breakpoints.down('700')]: {
    fontSize: '16px',
  },

  [theme.breakpoints.down('600')]: {
    display: 'none',
  },
}));

const UserAvatar = styled('div')(({ imgURL, theme }) => ({
  backgroundImage: `url(${imgURL})`,
  backgroundSize: 'cover',
  width: '40px',
  height: '40px',
  marginRight: '14px',
  borderRadius: '20px',

  [theme.breakpoints.down('1100')]: {
    fontSize: '15px',
    lineHeight: '20px',
    marginRight: '10px',
  },

  [theme.breakpoints.down('1000')]: {
    fontSize: '15px',
    lineHeight: '20px',
    marginRight: '14px',
  },

  [theme.breakpoints.down('900')]: {
    fontSize: '14px',
    lineHeight: '20px',
    marginRight: '14px',
  },

  [theme.breakpoints.down('800')]: {
    fontSize: '17px',
    marginRight: '14px',
  },

  [theme.breakpoints.down('700')]: {
    fontSize: '15px',
  },

  [theme.breakpoints.down('600')]: {
    fontSize: '16px',
  },
}));

const NoteImgContainer = styled('div')(({ imgURL, theme }) => ({
  width: '100%',
}));

const NoteImg = styled('div')(({ imgURL, theme }) => ({
  marginTop: '6px',
  display: 'block',
  alignItems: 'center',
  position: 'relative',
  marginBottom: '10px',
  '& img': {
    width: '100%',
    height: '100%',
    boxShadow: '0px 10px 6px #777777',
    borderRadius: '4px 4px 4px 4px',
  },

  [theme.breakpoints.down('600')]: {
    marginBottom: '0px',
    marginTop: '0px',
    backgroundImage: `url(${imgURL})`,
    backgroundSize: 'cover',
    // width: '296px',
    height: '296px',
    width: '100%',

    '& img': {
      // borderRadius: '0px'，
      display: 'none',
    },
  },

  [theme.breakpoints.down('550')]: {
    height: '270px',
    width: '100%',
  },

  [theme.breakpoints.down('500')]: {
    height: '250px',
    width: '100%',
  },

  [theme.breakpoints.down('450')]: {
    height: '230px',
    width: '100%',
  },

  [theme.breakpoints.down('400')]: {
    height: '200px',
    width: '100%',
  },

  [theme.breakpoints.down('350')]: {
    height: '160px',
    width: '100%',
  },
}));

const TextContainer = styled('span')({
  position: 'absolute',
  width: '94%',
  bottom: '2px',
  left: '3%',
  display: 'block',
});

const NoteTitleLable = styled('div')(({ theme }) => ({
  fontFamily: 'Fjalla One',
  letterSpacing: 0.3,
  fontSize: '15px',
  lineHeight: '22px',
  marginBottom: '5px',
  color: 'white',

  [theme.breakpoints.down('1200')]: {
    fontSize: '15px',
    lineHeight: '20px',
  },

  [theme.breakpoints.down('1100')]: {
    fontSize: '15px',
  },

  [theme.breakpoints.down('1000')]: {
    fontSize: '15px',
  },

  [theme.breakpoints.down('900')]: {
    fontSize: '14px',
  },

  [theme.breakpoints.down('800')]: {
    fontSize: '17px',
    lineHeight: '22px',
  },

  [theme.breakpoints.down('700')]: {
    fontSize: '14px',
    lineHeight: '20px',
  },

  [theme.breakpoints.down('600')]: {
    fontSize: '18px',
    lineHeight: '23px',
  },

  [theme.breakpoints.down('500')]: {
    fontSize: '16px',
    lineHeight: '22px',
  },

  [theme.breakpoints.down('400')]: {
    fontSize: '14px',
    lineHeight: '20px',
  },
}));

const NoteTime = styled('div')(({ theme }) => ({
  fontFamily: 'Raleway',
  fontSize: '12px',
  fontWeight: '400px',
  color: 'white',
  marginBottom: '5px',

  [theme.breakpoints.down('1100')]: {
    fontSize: '11px',
  },

  [theme.breakpoints.down('1000')]: {
    fontSize: '10px',
  },

  [theme.breakpoints.down('900')]: {
    fontSize: '10px',
  },

  [theme.breakpoints.down('800')]: {
    fontSize: '12px',
  },

  [theme.breakpoints.down('700')]: {
    fontSize: '12px',
  },

  [theme.breakpoints.down('600')]: {
    display: 'none',
  },
}));

const NoteCardAction = styled('div')(({ theme }) => ({
  alignItems: 'center',
  margin: '15px 1px 0px 5px',
  justifyContent: 'space-between',
  fontFamily: 'Raleway',
  color: '#AB9178',
  height: '18px',
  display: 'flex',
  flexDirection: 'row',
  [theme.breakpoints.down('600')]: {
    display: 'none',
  },
}));

const FunctionLeft = styled('div')({
  display: 'flex',
  fontSize: '18px',
  color: '#AB9178',
  alignItems: 'center',
});

const LikeNum = styled('div')({
  display: 'flex',
  alignItems: 'center',
  marginRight: '15px',
  marginLeft: '5px',
  marginBottom: '4px',
  fontSize: '15px',
});

const LikePeople = styled('div')(({ theme }) => ({
  fontFamily: 'Raleway',
  fontSize: '14px',
  margin: '8px 2px 5px 5px',
  color: 'black',
  // '& span': {
  //   fontWeight: 'bold',
  // },

  [theme.breakpoints.down('1100')]: {
    fontSize: '12px',
  },

  [theme.breakpoints.down('1000')]: {
    fontSize: '12px',
  },

  [theme.breakpoints.down('700')]: {
    fontSize: '10px',
  },
  [theme.breakpoints.down('600')]: {
    display: 'none',
  },
}));

const SocialBtn = styled(Button)({
  padding: '0',
  border: 'none',
  //cursor: 'pointer',
  color: '#AB9178',
  minWidth: 'auto',
  //marginBottom: '5px',
});

const LikePeopleBtn = styled('button')(({ theme }) => ({
  padding: '0',
  border: 'none',
  fontWeight: 'bold',
  cursor: 'pointer',
  fontFamily: 'Raleway',
  backgroundColor: 'transparent',
  fontSize: '14px',

  [theme.breakpoints.down('1100')]: {
    fontSize: '12px',
  },

  [theme.breakpoints.down('700')]: {
    fontSize: '10px',
  },
  [theme.breakpoints.down('600')]: {
    display: 'none',
  },
}));

const ResponsivelikedSocialNumber = styled('button')(({ theme }) => ({
  [theme.breakpoints.down('600')]: {
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
    background: 'transparent',
    border: 'none',
    alignItems: 'center',
    gap: '5vw',
  },
  display: 'none',
}));

const SocialBtnResponsive = styled('button')(({ theme }) => ({
  [theme.breakpoints.down('600')]: {
    background: 'transparent',
    padding: '0',
    border: 'none',
    cursor: 'pointer',
    color: '#AB9178',
  },
}));

const LikeNumResponsive = styled('div')(({ theme }) => ({
  alignItems: 'center',
  marginRight: '15px',
  marginLeft: '5px',
  marginBottom: '4px',
  fontSize: '14px',
  color: '#AB9178',

  [theme.breakpoints.down('500')]: {
    fontSize: '14px',
    marginRight: '15px',
  },

  [theme.breakpoints.down('400')]: {
    fontSize: '12px',
  },

  [theme.breakpoints.down('350')]: {
    fontSize: '10px',
  },
}));

const ResponsiveNoteAuthor = styled('div')(({ theme }) => ({
  fontSize: '16px',
  color: '#AB9178',
  fontFamily: 'Raleway',
  marginBottom: '4px',
  marginLeft: '-4px',
  [theme.breakpoints.down('500')]: {
    fontSize: '13px',
  },

  [theme.breakpoints.down('400')]: {
    fontSize: '11px',
  },

  [theme.breakpoints.down('350')]: {
    fontSize: '10px',
  },
}));

const ResponsiveDiv = styled('button')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  background: 'transparent',
  border: 'none',
  alignItems: 'center',
}));

const GalleryCardLayout = ({ props }) => {
  const imgURL = () => {
    const defaultImg = props.author.avatar;
    if (
      props.attachments == undefined ||
      props.attachments == null ||
      props.attachments.length <= 0
    ) {
      //return default img: authors avatar
      return defaultImg;
    } else {
      return props.attachments[0].url;
    }
  };

  const likedUser = () => {
    const defaultLikedUser = 'None';
    if (props.likedUsers == undefined || props.likedUsers == null || props.likedUsers.length <= 0) {
      //return default text: None
      return defaultLikedUser;
    } else {
      return props.likedUsers[0].userName;
    }
  };

  const noteId = props._id;
  const { likedUsers, savedUsers } = props;
  const [likeNote] = useLikeNoteMutation();
  const [unlikeNote] = useUnlikeNoteMutation();
  const [saveNote] = useSaveNoteMutation();
  const [unsaveNote] = useUnsaveNoteMutation();
  const currentUserId = useSelector(selectUser);

  const likeOrUnlike = async () => {
    if (likedUsers.find((ele) => ele._id === currentUserId)) {
      await unlikeNote({ noteId, userId: currentUserId });
    } else {
      await likeNote({ noteId, userId: currentUserId });
    }
  };

  const saveOrUnsave = async () => {
    if (savedUsers.includes(currentUserId)) {
      await unsaveNote({ noteId, userId: currentUserId });
    } else {
      await saveNote({ noteId, userId: currentUserId });
    }
  };

  const linkUrl = `/note/${props._id}`;

  const postedDate = () => {
    let year = '';
    let month = '';
    let day = '';
    let date = '';
    if (props.date == undefined || props.date == null || props.date.length <= 0) {
      date = '2 mins ago';
      return date;
    } else {
      year = props.date.split('-')[0];
      month = toMonthShortName(parseInt(props.date.split('-')[1]));
      day = props.date.split('-')[2].split('T')[0];
      date = day + ' ' + month + ', ' + year;
      return date;
    }
  };

  return (
    <NoteCardContainer elevation={3}>
      <NoteAuthor>
        <UserAvatar imgURL={props.author.avatar}></UserAvatar>
        {props.author.userName}
      </NoteAuthor>
      <Link href={linkUrl} underline="none">
        <NoteImgContainer>
          <NoteImg imgURL={imgURL()}>
            <img src={imgURL()} alt=""></img>
            <EffectLayer>
              <ShadowLayer>
                <YellowLine>
                  <TextContainer>
                    <NoteTitleLable>{props.title}</NoteTitleLable>
                    <NoteTime>{postedDate()}</NoteTime>
                    <ResponsivelikedSocialNumber>
                      <ResponsiveNoteAuthor>{props.author.userName}</ResponsiveNoteAuthor>
                      <ResponsiveDiv>
                        <SocialBtnResponsive onClick={likeOrUnlike}>
                          {likedUsers.find((ele) => ele._id === currentUserId) ? (
                            <ThumbUpAltIcon style={{ width: '17px' }} />
                          ) : (
                            <ThumbUpOffAltIcon style={{ width: '17px' }} />
                          )}
                        </SocialBtnResponsive>
                        <LikeNumResponsive>{props.likedUsers.length}</LikeNumResponsive>
                      </ResponsiveDiv>
                    </ResponsivelikedSocialNumber>
                  </TextContainer>
                </YellowLine>
              </ShadowLayer>
            </EffectLayer>
          </NoteImg>
        </NoteImgContainer>
      </Link>

      <NoteCardAction>
        <FunctionLeft>
          <SocialBtn onClick={likeOrUnlike}>
            {likedUsers.find((ele) => ele._id === currentUserId) ? (
              <ThumbUpAltIcon />
            ) : (
              <ThumbUpOffAltIcon />
            )}
          </SocialBtn>
          <LikeNum>{props.likedUsers.length}</LikeNum>
          <SocialBtn>
            <ShareOutlinedIcon />
          </SocialBtn>
        </FunctionLeft>

        <SocialBtn onClick={saveOrUnsave}>
          {savedUsers.includes(currentUserId) ? <BookIcon /> : <ClassOutlinedIcon />}
        </SocialBtn>
      </NoteCardAction>

      <LikePeople>
        Liked by <LikePeopleBtn>{likedUser()}</LikePeopleBtn> and{' '}
        <LikePeopleBtn>Others</LikePeopleBtn>
      </LikePeople>
    </NoteCardContainer>
  );
};

export default GalleryCardLayout;
